import type { Environment } from '../types/environment'

// Environment URLs
const STAGING_URL = 'https://dextra.eu.ustage.app'
const PRODUCTION_URL = 'https://dextra.eu.unblu.app'

// Environment configuration
const environments: Record<string, Environment> = {
  staging: {
    name: 'staging',
    unbluApiUrl: `${STAGING_URL}/unblu/js-api/v8/branch-client/branch-client-app-component.min.js`,
    baseUrl: STAGING_URL,
    entryPath: '/unblu',
  },
  production: {
    name: 'production',
    unbluApiUrl: `${PRODUCTION_URL}/unblu/js-api/v8/branch-client/branch-client-app-component.min.js`,
    baseUrl: PRODUCTION_URL,
    entryPath: '/unblu',
  }
}

let _envResolved: Environment | null = null

function resolveCustomDomainOverride(rawDomain: string): Environment | null {
  const domain = rawDomain.trim()
  if (!domain) return null

  // Supports ?unblu-domain=https://host.tld and ?unblu-domain=host.tld
  const withProtocol = /^https?:\/\//i.test(domain) ? domain : `https://${domain}`
  try {
    const url = new URL(withProtocol)
    const baseUrl = `${url.protocol}//${url.host}`
    return {
      name: 'custom',
      unbluApiUrl: `${baseUrl}/unblu/js-api/v8/branch-client/branch-client-app-component.min.js`,
      baseUrl,
      entryPath: '/unblu',
    }
  } catch {
    return null
  }
}

// Runtime environment detection (logs only on first call)
export function getCurrentEnvironment(): Environment {
  if (_envResolved) return _envResolved

  const hostname = window.location.hostname
  const urlParams = new URLSearchParams(window.location.search)

  // TEMP: direct Unblu domain override
  const unbluDomainOverride = urlParams.get('unblu-domain')
  if (unbluDomainOverride) {
    const resolved = resolveCustomDomainOverride(unbluDomainOverride)
    if (resolved) {
      console.warn(`Unblu domain override: ?unblu-domain=${unbluDomainOverride} -> ${resolved.baseUrl}`)
      _envResolved = resolved
      return _envResolved
    }
    console.warn(`Invalid Unblu domain override ignored: ?unblu-domain=${unbluDomainOverride}`)
  }

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const apiKey = urlParams.get('api-key')
    
    if (apiKey === 'fUa3bsrqRkC6LDDcu4tqtA') {
      console.debug('Local development: Using PRODUCTION API (detected by API key)')
      _envResolved = environments.production
    } else if (apiKey === 'XNCEs0ZbRG-L9P9GYvGnbQ') {
      console.debug('Local development: Using STAGING API (detected by API key)')
      _envResolved = environments.staging
    } else {
      console.debug('Local development: Using STAGING API (fallback for unknown API key)')
      _envResolved = environments.staging
    }
  } else if (hostname.includes('uenv.dev')) {
    console.debug('Detected DEV environment (uhp): Using STAGING API')
    _envResolved = { ...environments.staging, name: 'dev' }
  } else if (hostname.includes('ustage.app')) {
    console.debug('Detected STAGING environment')
    _envResolved = environments.staging
  } else if (hostname.includes('unblu.app')) {
    console.debug('Detected PRODUCTION environment')
    _envResolved = environments.production
  } else {
    console.warn('Unknown environment, falling back to STAGING')
    _envResolved = environments.staging
  }

  return _envResolved
}

// Helper function to get the correct base href for the current environment
export const getBaseHref = (): string => {
  const env = getCurrentEnvironment()
  return env.entryPath
}

// Export environments for reference
export { environments } 